import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { loadDemoMode } from './utils/demoMode';
import Home from './pages/Home';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import DemoSignupPage from './pages/DemoSignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import FranchiseesPage from './pages/FranchiseesPage';
import AddFranchiseePage from './pages/AddFranchiseePage';
import AllowedEmailsPage from './pages/AllowedEmailsPage';
import TerritoriesPage from './pages/TerritoriesPage';
import ClientsPage from './pages/ClientsPage'; // This is for Suppliers (original Clients)
import AddClientPage from './pages/AddClientPage'; // This is for Suppliers (original Clients)
// Revert Household imports to match filenames
import HouseholdsPage from './pages/HouseholdsPage'; // Reverted import name
import AddHouseholdPage from './pages/AddHouseholdPage'; // Reverted import name
import JobSchedulerPage from './pages/JobSchedulerPage';
import JobsListPage from './pages/JobsListPage';
import AddJobSchedulePage from './pages/AddJobSchedulePage';
import ServiceTypesPage from './pages/ServiceTypesPage';
import SettingsPage from './pages/SettingsPage';
import EditFranchiseePage from './pages/EditFranchiseePage'; // Import the new edit page
import EditClientPage from './pages/EditClientPage'; // Import the new client edit page
import EditHouseholdPage from './pages/EditHouseholdPage'; // Import the new household edit page
import EditJobSchedulePage from './pages/EditJobSchedulePage'; // Import the new schedule edit page
import EditJobPage from './pages/EditJobPage'; // Import the new job edit page
import ImportXeroContactsPage from './pages/ImportXeroContactsPage'; // Import the new Xero contacts import page
import XeroContactDetailsPage from './pages/XeroContactDetailsPage'; // Import the Xero contact details page
import InvoicesPage from './pages/InvoicesPage'; // Import the new InvoicesPage
import InvoiceDetailsPage from './pages/InvoiceDetailsPage'; // Import the new InvoiceDetailsPage
import RCTIDetailsPage from './pages/RCTIDetailsPage'; // Import the new RCTIDetailsPage
import ChartOfAccountsPage from './pages/ChartOfAccountsPage'; // Import the new ChartOfAccountsPage
import UtilitiesPage from './pages/UtilitiesPage'; // Import the new UtilitiesPage
import ImportJobSchedulesHistoryPage from './pages/ImportJobSchedulesHistoryPage';
import ImportJobSchedulesHistoryDetailPage from './pages/ImportJobSchedulesHistoryDetailPage';
import ReportsPage from './pages/ReportsPage'; // Import the new ReportsPage
import ActivityLogPage from './pages/ActivityLogPage'; // Import the Activity Log page
import ForgotPasswordPage from './pages/ForgotPasswordPage'; // Import the new ForgotPasswordPage
import AccountPage from './pages/AccountPage'; // Import the new AccountPage
import { AppConfigProvider } from './context/AppConfigContext'; // Import the provider
import { UserProvider } from './context/UserContext'; // Import the UserProvider
import './firebase'; // Initialize Firebase

function App() {
  const [demoMode, setDemoMode] = useState(null);
  useEffect(() => { loadDemoMode().then(setDemoMode); }, []);
  const isDevEnvironment = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'loope-dev-env';

  return (
    <AppConfigProvider>
      <UserProvider>
      <Router>
        <div className="min-h-screen bg-gray-100">
          {/* Demo Mode Banner */}
          {demoMode === true && (
            <div className="bg-orange-500 text-white text-center py-2 px-4 text-sm font-bold sticky top-0 z-50 shadow-md">
              🎭 DEMO MODE — Connected to demo database. Data is not real.
            </div>
          )}
          {/* Dev Environment Banner */}
          {isDevEnvironment && demoMode !== true && (
            <div className="bg-yellow-500 text-white text-center py-1 px-4 text-sm font-medium sticky top-0 z-50 shadow-md">
              🧪 TEST ENVIRONMENT - Development Mode
            </div>
          )}
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/demo-signup" element={<DemoSignupPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            {/* Franchisee Routes */}
            <Route
              path="/franchisees"
              element={
                <ProtectedRoute>
                  <FranchiseesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/franchisees/add"
              element={
                <ProtectedRoute>
                  <AddFranchiseePage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Editing Franchisee */}
            <Route
              path="/franchisees/edit/:id"
              element={
                <ProtectedRoute>
                  <EditFranchiseePage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Allowed Emails */}
            <Route
              path="/franchisees/allowed-emails"
              element={
                <ProtectedRoute>
                  <AllowedEmailsPage />
                </ProtectedRoute>
              }
            />
            {/* Add Territories Route */}
            <Route
              path="/territories"
              element={
                <ProtectedRoute>
                  <TerritoriesPage />
                </ProtectedRoute>
              }
            />
            {/* Client (Supplier) Routes - Keep path /clients */}
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/add"
              element={
                <ProtectedRoute>
                  <AddClientPage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Editing Client */}
            <Route
              path="/clients/edit/:id"
              element={
                <ProtectedRoute>
                  <EditClientPage />
                </ProtectedRoute>
              }
            />
            {/* Client (formerly Household) Routes - Keep path /households for now */}
            <Route
              path="/households"
              element={
                <ProtectedRoute>
                  <HouseholdsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/households/add"
              element={
                <ProtectedRoute>
                  <AddHouseholdPage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Editing Household */}
            <Route
              path="/households/edit/:id"
              element={
                <ProtectedRoute>
                  <EditHouseholdPage />
                </ProtectedRoute>
              }
            />
            {/* Job Scheduler Routes */}
            <Route
              path="/job-scheduler"
              element={
                <ProtectedRoute>
                  <JobSchedulerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-scheduler/add"
              element={
                <ProtectedRoute>
                  <AddJobSchedulePage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Editing Job Schedule */}
            <Route
              path="/job-scheduler/edit/:id"
              element={
                <ProtectedRoute>
                  <EditJobSchedulePage />
                </ProtectedRoute>
              }
            />
            {/* Jobs List Route */}
            <Route
              path="/jobs-list"
              element={
                <ProtectedRoute>
                  <JobsListPage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Editing Job */}
            <Route
              path="/jobs-list/edit/:id"
              element={
                <ProtectedRoute>
                  <EditJobPage />
                </ProtectedRoute>
              }
            />
            {/* Service Types Route */}
            <Route
              path="/services"
              element={
                <ProtectedRoute>
                  <ServiceTypesPage />
                </ProtectedRoute>
              }
            />
            {/* Invoice Routes */}
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:invoiceId"
              element={
                <ProtectedRoute>
                  <InvoiceDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* RCTI Routes */}
            <Route
              path="/rctis/:rctiId"
              element={
                <ProtectedRoute>
                  <RCTIDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* Chart of Accounts Route */}
            <Route
              path="/chart-of-accounts"
              element={
                <ProtectedRoute>
                  <ChartOfAccountsPage />
                </ProtectedRoute>
              }
            />
            {/* Settings Route */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            {/* Utilities Route */}
            <Route
              path="/utilities"
              element={
                <ProtectedRoute>
                  <UtilitiesPage />
                </ProtectedRoute>
              }
            />
            {/* Import Job Schedules History Routes */}
            <Route
              path="/utilities/import-job-schedules/history"
              element={
                <ProtectedRoute>
                  <ImportJobSchedulesHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/utilities/import-job-schedules/history/:id"
              element={
                <ProtectedRoute>
                  <ImportJobSchedulesHistoryDetailPage />
                </ProtectedRoute>
              }
            />
            {/* Activity Log Route */}
            <Route
              path="/activity-log"
              element={
                <ProtectedRoute>
                  <ActivityLogPage />
                </ProtectedRoute>
              }
            />
            {/* Reports Route */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Importing Xero Contacts */}
            <Route
              path="/settings/xero-import-contacts"
              element={
                <ProtectedRoute>
                  <ImportXeroContactsPage />
                </ProtectedRoute>
              }
            />
            {/* New Route for Xero Contact Details */}
            <Route
              path="/settings/xero-contact-details/:contactId"
              element={
                <ProtectedRoute>
                  <XeroContactDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* Add other module routes here later */}
          </Routes>
        </div>
      </Router>
      </UserProvider>
    </AppConfigProvider>
  );
}

export default App;
